import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserId } from '@/services/data/auth';
import { TicketTeam, TicketPriority, createTicket } from './tickets';
import { emitTicketsChanged, emitProposalsChanged, subscribeToProposalsChanged } from '@/lib/tickets-events';

export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'modified';

export interface AITicketProposal {
  id: string;
  message_id: string;
  conversation_id: string;
  suggested_title: string;
  highlighted_text: string;
  suggested_priority: TicketPriority;
  suggested_team: TicketTeam;
  confidence_score: number;
  needs_review: boolean;
  ai_reasoning: string | null;
  full_context_used: boolean;
  status: ProposalStatus;
  validated_by: string | null;
  validated_at: string | null;
  rejection_reason: string | null;
  created_ticket_id: string | null;
  created_at: string;
  updated_at: string;
  suggested_responses: string[] | null;
  // Joined data
  message?: {
    id: string;
    content: string | null;
    sender_id: string;
    created_at: string;
    sender?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      avatar_url: string | null;
    };
  };
  conversation?: {
    id: string;
    customer_id: string;
    customer?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      email: string;
      avatar_url: string | null;
    };
  };
}

export interface AIPromptSettings {
  id: string;
  system_prompt: string;
  role_context: string | null;
  context_messages_count: number;
  min_confidence_threshold: number;
  version: number;
  updated_by: string | null;
  updated_at: string;
  is_active: boolean;
}

/**
 * Get AI proposals with optional status filter
 */
export async function getAIProposals(status: 'pending' | 'all' = 'pending'): Promise<{ data: AITicketProposal[]; error: string | null }> {
  let query = supabase
    .from('ai_ticket_proposals')
    .select(`
      *,
      message:chat_messages!ai_ticket_proposals_message_id_fkey(
        id, content, sender_id, created_at,
        sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url)
      ),
      conversation:conversations!ai_ticket_proposals_conversation_id_fkey(
        id, customer_id,
        customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url)
      )
    `)
    .order('created_at', { ascending: false });

  if (status === 'pending') {
    query = query.eq('status', 'pending');
  }

  const { data, error } = await query;

  if (error) return { data: [], error: error.message };
  
  // Sort: needs_review first, then by confidence descending
  const sorted = (data || []).sort((a, b) => {
    // Pending first
    if (a.status === 'pending' && b.status !== 'pending') return -1;
    if (a.status !== 'pending' && b.status === 'pending') return 1;
    // needs_review first within pending
    if (a.needs_review && !b.needs_review) return -1;
    if (!a.needs_review && b.needs_review) return 1;
    // Higher confidence first
    return (b.confidence_score || 0) - (a.confidence_score || 0);
  });

  return { data: sorted as AITicketProposal[], error: null };
}

/**
 * Get count of pending proposals
 */
export async function getPendingProposalCount(): Promise<number> {
  const { count, error } = await supabase
    .from('ai_ticket_proposals')
    .select('*', { count: 'exact', head: true })
    .eq('status', 'pending');

  if (error) return 0;
  return count || 0;
}

/**
 * Accept a proposal and create a ticket
 */
export async function acceptProposal(
  proposalId: string,
  modifications?: {
    title?: string;
    highlightedText?: string;
    priority?: TicketPriority;
    team?: TicketTeam;
  }
): Promise<{ data: { ticketId: string } | null; error: string | null }> {
  const userId = await getCurrentUserId();
  if (!userId) return { data: null, error: 'Not authenticated' };

  // Get the proposal
  const { data: proposal, error: fetchError } = await supabase
    .from('ai_ticket_proposals')
    .select('*')
    .eq('id', proposalId)
    .single();

  if (fetchError || !proposal) {
    return { data: null, error: fetchError?.message || 'Proposal not found' };
  }

  // Create the ticket with AI reasoning as internal note
  const aiNote = proposal.ai_reasoning 
    ? `🤖 PRSNL IA : ${proposal.ai_reasoning}` 
    : null;
    
  const ticketResult = await createTicket({
    conversationId: proposal.conversation_id,
    messageId: proposal.message_id,
    selectedText: modifications?.highlightedText || proposal.highlighted_text,
    assignedTeam: (modifications?.team || proposal.suggested_team) as TicketTeam,
    priority: (modifications?.priority || proposal.suggested_priority) as TicketPriority,
    title: modifications?.title || proposal.suggested_title,
    internalNote: aiNote,
    suggestedResponses: proposal.suggested_responses || [],
  });

  if (ticketResult.error || !ticketResult.data) {
    return { data: null, error: ticketResult.error || 'Failed to create ticket' };
  }

  // Update the proposal status
  const newStatus = modifications ? 'modified' : 'accepted';
  const { error: updateError } = await supabase
    .from('ai_ticket_proposals')
    .update({
      status: newStatus,
      validated_by: userId,
      validated_at: new Date().toISOString(),
      created_ticket_id: ticketResult.data.id,
    })
    .eq('id', proposalId);

  if (updateError) {
    return { data: null, error: updateError.message };
  }

  emitTicketsChanged();
  emitProposalsChanged();
  return { data: { ticketId: ticketResult.data.id }, error: null };
}

/**
 * Reject a proposal
 */
export async function rejectProposal(
  proposalId: string,
  reason?: string
): Promise<{ error: string | null }> {
  const userId = await getCurrentUserId();
  if (!userId) return { error: 'Not authenticated' };

  const { error } = await supabase
    .from('ai_ticket_proposals')
    .update({
      status: 'rejected',
      validated_by: userId,
      validated_at: new Date().toISOString(),
      rejection_reason: reason || null,
    })
    .eq('id', proposalId);

  if (!error) {
    emitProposalsChanged();
  }
  
  return { error: error?.message || null };
}

/**
 * Subscribe to proposal updates (Realtime postgres_changes, broadcast, and local events)
 */
export function subscribeToProposals(onUpdate: () => void) {
  let debounceTimer: NodeJS.Timeout | null = null;

  const debouncedUpdate = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(onUpdate, 300);
  };

  const channelId = `proposals:all:${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

  // Subscribe to Realtime postgres_changes
  const channel = supabase
    .channel(channelId)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'ai_ticket_proposals',
      },
      () => {
        debouncedUpdate();
      }
    )
    .subscribe();

  // Subscribe to broadcast channel for new proposals from Edge Functions
  const broadcastChannel = supabase
    .channel('proposals:new')
    .on('broadcast', { event: 'new_proposals' }, () => {
      console.log('[subscribeToProposals] Received new_proposals broadcast');
      debouncedUpdate();
    })
    .subscribe();

  // Also subscribe to local events for instant same-tab updates
  const unsubscribeLocal = subscribeToProposalsChanged(debouncedUpdate);

  return () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    supabase.removeChannel(channel);
    supabase.removeChannel(broadcastChannel);
    unsubscribeLocal();
  };
}

/**
 * Get active prompt settings
 */
export async function getPromptSettings(): Promise<{ data: AIPromptSettings | null; error: string | null }> {
  const { data, error } = await supabase
    .from('ai_prompt_settings')
    .select('*')
    .eq('is_active', true)
    .single();

  if (error && error.code !== 'PGRST116') {
    return { data: null, error: error.message };
  }

  return { data: data as AIPromptSettings | null, error: null };
}

/**
 * Get all prompt settings versions (for history)
 */
export async function getPromptSettingsHistory(): Promise<{ data: AIPromptSettings[]; error: string | null }> {
  const { data, error } = await supabase
    .from('ai_prompt_settings')
    .select('*')
    .order('version', { ascending: false });

  if (error) return { data: [], error: error.message };
  return { data: (data || []) as AIPromptSettings[], error: null };
}

/**
 * Update prompt settings (creates a new version)
 */
export async function updatePromptSettings(
  settings: {
    system_prompt: string;
    role_context?: string | null;
    context_messages_count?: number;
    min_confidence_threshold?: number;
  }
): Promise<{ error: string | null }> {
  const userId = await getCurrentUserId();
  if (!userId) return { error: 'Not authenticated' };

  // Get current active version
  const { data: current } = await supabase
    .from('ai_prompt_settings')
    .select('version')
    .eq('is_active', true)
    .single();

  const newVersion = (current?.version || 0) + 1;

  // Deactivate current
  await supabase
    .from('ai_prompt_settings')
    .update({ is_active: false })
    .eq('is_active', true);

  // Insert new version
  const { error } = await supabase
    .from('ai_prompt_settings')
    .insert({
      system_prompt: settings.system_prompt,
      role_context: settings.role_context || null,
      context_messages_count: settings.context_messages_count || 5,
      min_confidence_threshold: settings.min_confidence_threshold || 0.6,
      version: newVersion,
      updated_by: userId,
      is_active: true,
    });

  return { error: error?.message || null };
}

/**
 * Get proposal statistics
 */
export async function getProposalStats(): Promise<{
  total: number;
  accepted: number;
  rejected: number;
  pending: number;
  avgConfidence: number;
}> {
  const { data, error } = await supabase
    .from('ai_ticket_proposals')
    .select('status, confidence_score');

  if (error || !data) {
    return { total: 0, accepted: 0, rejected: 0, pending: 0, avgConfidence: 0 };
  }

  const total = data.length;
  const accepted = data.filter(p => p.status === 'accepted' || p.status === 'modified').length;
  const rejected = data.filter(p => p.status === 'rejected').length;
  const pending = data.filter(p => p.status === 'pending').length;
  const avgConfidence = total > 0
    ? data.reduce((sum, p) => sum + (p.confidence_score || 0), 0) / total
    : 0;

  return { total, accepted, rejected, pending, avgConfidence };
}

// Re-export for convenience
export { TICKET_TEAMS, TICKET_PRIORITIES } from './tickets';
